<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import RoomForm from './RoomForm.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedRoom = ref({});
const isSubmitting = ref(false);

const records = computed(() => getters['hotelRooms/getRooms'].value);
const uiFlags = computed(() => getters['hotelRooms/getUIFlags'].value);

const tableHeaders = computed(() => [
  t('HOTEL_TARIFF.TABLE.NAME'),
  t('HOTEL_TARIFF.TABLE.UNITS'),
  t('HOTEL_TARIFF.TABLE.MAX_GUESTS'),
  t('HOTEL_TARIFF.TABLE.FREE_AGE'),
  t('HOTEL_TARIFF.TABLE.PRICE_1'),
  t('HOTEL_TARIFF.TABLE.PRICE_2'),
  t('HOTEL_TARIFF.TABLE.PRICE_3'),
  t('HOTEL_TARIFF.TABLE.AVAILABILITY'),
  t('HOTEL_TARIFF.TABLE.STATUS'),
  t('HOTEL_TARIFF.TABLE.ACTIONS'),
]);

const countFutureDates = room => {
  if (!room.available_dates?.length) return 0;
  const today = new Date().toISOString().split('T')[0];
  return room.available_dates.filter(d => d >= today).length;
};

const formatCurrency = value => {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = room => {
  selectedRoom.value = room;
  showEditPopup.value = true;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = room => {
  selectedRoom.value = room;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const createRoom = async formData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('hotelRooms/create', formData);
    useAlert(t('HOTEL_TARIFF.ADD.API.SUCCESS_MESSAGE'));
    hideAddPopup();
  } catch (error) {
    useAlert(error?.message || t('HOTEL_TARIFF.ADD.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

const updateRoom = async formData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('hotelRooms/update', {
      id: selectedRoom.value.id,
      ...formData,
    });
    useAlert(t('HOTEL_TARIFF.EDIT.API.SUCCESS_MESSAGE'));
    hideEditPopup();
  } catch (error) {
    useAlert(error?.message || t('HOTEL_TARIFF.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

const confirmDeletion = async () => {
  loading.value[selectedRoom.value.id] = true;
  closeDeletePopup();
  try {
    await store.dispatch('hotelRooms/delete', selectedRoom.value.id);
    useAlert(t('HOTEL_TARIFF.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('HOTEL_TARIFF.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedRoom.value.id] = false;
  }
};

onBeforeMount(() => {
  store.dispatch('hotelRooms/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('HOTEL_TARIFF.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('HOTEL_TARIFF.NO_ROOMS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('HOTEL_TARIFF.HEADER')"
        :description="$t('HOTEL_TARIFF.ADD.DESC')"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ records.length }}
            {{ records.length === 1 ? 'quarto' : 'quartos' }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('HOTEL_TARIFF.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="records"
        :no-data-message="$t('HOTEL_TARIFF.NO_ROOMS')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="room in items"
            :key="room.id"
            :item="room"
          >
            <template #default>
              <BaseTableCell>
                <div>
                  <span class="text-body-main text-n-slate-12 font-medium">
                    {{ room.name }}
                  </span>
                  <p
                    v-if="room.description"
                    class="text-xs text-n-slate-10 truncate max-w-[200px]"
                  >
                    {{ room.description }}
                  </p>
                </div>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ room.total_units || 1 }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ room.max_guests }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ room.free_age_limit > 0 ? `${room.free_age_limit} ${$t('HOTEL_TARIFF.TABLE.YEARS')}` : '-' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ formatCurrency(room.price_1_night) }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ formatCurrency(room.price_2_nights) }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ formatCurrency(room.price_3_nights) }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  :class="
                    countFutureDates(room) > 0
                      ? 'bg-n-teal-2 text-n-teal-11'
                      : 'bg-n-amber-2 text-n-amber-11'
                  "
                >
                  {{ countFutureDates(room) }} dias
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  :class="
                    room.active
                      ? 'bg-n-teal-2 text-n-teal-11'
                      : 'bg-n-slate-3 text-n-slate-11'
                  "
                >
                  {{ room.active ? $t('HOTEL_TARIFF.TABLE.ACTIVE') : $t('HOTEL_TARIFF.TABLE.INACTIVE') }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[room.id]"
                    @click="openEditPopup(room)"
                  />
                  <Button
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[room.id]"
                    @click="openDeletePopup(room)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <RoomForm
        :is-submitting="isSubmitting"
        @submit="createRoom"
        @close="hideAddPopup"
      />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <RoomForm
        :room="selectedRoom"
        :is-submitting="isSubmitting"
        @submit="updateRoom"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('HOTEL_TARIFF.DELETE.TITLE')"
      :message="$t('HOTEL_TARIFF.DELETE.DESCRIPTION', { roomName: selectedRoom.name })"
      :confirm-text="$t('HOTEL_TARIFF.DELETE.CONFIRM.YES')"
      :reject-text="$t('HOTEL_TARIFF.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
